<template>
    <div class="manual-step flex flex-col h-full">
        <!-- Step 2: Date opened -->
        <template v-if="currentStep === 2">
            <div
                class="modal-body flex flex-col flex-1 min-h-0 overflow-y-auto pb-24 md:pb-0"
            >
                <div
                    class="mb-10 flex items-center"
                    v-if="manualChosenCard"
                >
                    <img
                        :src="`${cdnUrl}/${manualChosenCard.card_image?.path}/_small/${manualChosenCard.card_image?.file_name}`"
                        :alt="manualChosenCard.title"
                        class="inline-block"
                        style="
                            max-width: 75px;
                            height: auto;
                            margin-right: 12px;
                        "
                        v-if="manualChosenCard.card_image"
                    />
                    <p class="leading-tight">
                        {{ manualChosenCard.title }}
                    </p>
                </div>

                <p class="mb-5 text-center">
                    <label class="flex flex-col items-center w-full">
                        <span class="block w-full mb-5 text-lg md:text-2xl">
                            When did you open this card?
                        </span>
                        <UiInput
                            id="date_opened"
                            name="date_opened"
                            label="When did you open this card?"
                            placeholder="Date opened"
                            :options="{
                                icon: 'calendar',
                                hideLabel: true,
                            }"
                            class="w-full"
                        >
                            <template #input>
                                <flat-pickr
                                    :config="dateConfig"
                                    class="form-input date-picker flatpickr-input active border shadow-sm !pr-9 !pl-10 w-full disabled:cursor-not-allowed"
                                    v-model="manualCard.dateOpened"
                                    placeholder="Select Date ..."
                                    :disabled="manualCard.opened_long_ago"
                                ></flat-pickr>
                            </template>
                        </UiInput>
                    </label>
                    <label
                        class="w-full flex justify-center text-left pt-5"
                    >
                        <input
                            type="checkbox"
                            value="1"
                            v-model="manualCard.opened_long_ago"
                        />
                        <span class="ml-2 text-sm leading-tight">
                            I'm not sure
                            <br />
                            <small>(it was a few years ago)</small>
                        </span>
                    </label>
                </p>
                <p class="text-xs text-center px-5 mb-10">
                    Choose the date you were approved for the card, not
                    application date, or when you received it in the mail.
                </p>
                <div class="w-full py-2 my-2" v-show="errors.length > 0">
                    <Errors :errors="errors" />
                </div>
            </div>
            <div
                class="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] z-[1000]"
            >
                <div class="grid grid-cols-2 gap-2">
                    <button
                        type="button"
                        @click.prevent="$emit('gotoManualStep', 1)"
                        class="w-full py-3 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700"
                        id="hs-wallet-manual-card-entry-back-mobile-step2"
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        @click.prevent="$emit('gotoManualStep', 3)"
                        class="w-full py-3 rounded-md bg-slate-800 text-white text-sm font-medium leading-5 hover:bg-slate-700"
                        id="hs-wallet-manual-card-entry-next-mobile-step2"
                    >
                        Next
                    </button>
                </div>
            </div>
            <div
                class="hidden md:flex justify-between items-center p-6 bg-white dark:bg-slate-800 flex-shrink-0 mt-auto w-full"
            >
                <a
                    href="#manual-step-1"
                    @click.prevent="$emit('gotoManualStep', 1)"
                    class="btn-base btn-white !w-48"
                    id="hs-wallet-manual-card-entry-back"
                >
                    &larr; Back
                </a>
                <a
                    href="#manual-step-3"
                    class="btn-base btn-primary !w-48"
                    @click.prevent="$emit('gotoManualStep', 3)"
                    id="hs-wallet-manual-card-entry-next"
                >
                    Next
                </a>
            </div>
        </template>

        <!-- Step 3: Card details -->
        <template v-else>
            <!-- Main Container -->
            <div
                class="flex p-6 flex-col items-center gap-8 rounded-none md:rounded-lg bg-white dark:bg-slate-800 shadow-none md:shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_0_rgba(0,0,0,0.06)] pb-24 md:pb-6 flex-1 min-h-0 overflow-y-auto"
            >
                <!-- Header Container -->
                <div class="flex items-start justify-between self-stretch">
                    <div class="flex flex-col items-start gap-1 flex-1">
                        <!-- Title -->
                        <h1
                            class="self-stretch text-slate-800 dark:text-slate-100 text-xl font-medium leading-7"
                        >
                            Add card
                        </h1>

                        <!-- Card name and last four -->
                        <p
                            class="text-slate-500 dark:text-slate-400 flex items-center gap-2 text-sm font-normal leading-5"
                            v-if="manualChosenCard"
                        >
                            <img
                                v-if="manualChosenCard.card_image"
                                :src="`${cdnUrl}/${manualChosenCard.card_image.path}/_small/${manualChosenCard.card_image.file_name}`"
                                :alt="selectedCardAndUserHeader"
                                class="w-8"
                            />
                            <span class="inline-block">
                                {{ selectedCardAndUserHeader }}
                            </span>
                        </p>
                    </div>
                    <button
                        type="button"
                        @click="$emit('close')"
                        class="btn-icon btn-transparent w-10"
                    >
                        <Icon icon="close" />
                    </button>
                </div>

                <!-- Mobile step lines (Step 2 active) -->
                <div
                    class="flex md:hidden px-6 pb-4 justify-center items-center gap-2 self-stretch"
                >
                    <template
                        v-if="manualCard.accountType === 'authorized_user'"
                    >
                        <div class="flex-1 h-1 rounded bg-slate-200"></div>
                        <div class="flex-1 h-1 rounded bg-slate-800"></div>
                    </template>
                    <template v-else>
                        <div class="flex-1 h-1 rounded bg-slate-200"></div>
                        <div class="flex-1 h-1 rounded bg-slate-800"></div>
                        <div class="flex-1 h-1 rounded bg-slate-200"></div>
                    </template>
                </div>

                <!-- Form Container -->
                <div class="flex flex-col items-start gap-6 self-stretch">
                    <!-- Card Status -->
                    <div
                        class="flex flex-col items-start self-stretch sm:flex-row sm:gap-6"
                    >
                        <!-- Left container with text -->
                        <div
                            class="flex pb-2 flex-col items-start gap-1 self-stretch sm:flex-1 sm:justify-center sm:pb-0"
                        >
                            <p
                                class="text-sm font-medium text-slate-800 dark:text-slate-100 hidden sm:block"
                            >
                                Card status
                            </p>
                            <p
                                class="text-sm font-medium leading-5 text-slate-600 dark:text-slate-300 block sm:hidden"
                            >
                                Card status
                            </p>
                            <p
                                class="text-sm text-slate-500 dark:text-slate-400 hidden sm:block"
                            >
                                Set your card status as open or closed
                            </p>
                        </div>

                        <!-- Right container with toggle buttons -->
                        <div>
                            <div
                                class="flex items-start rounded bg-slate-100 dark:bg-slate-700"
                            >
                                <!-- Open button -->
                                <button
                                    type="button"
                                    @click="manualCard.cardStatus = 'open'"
                                    :class="[
                                        'flex flex-1 items-center justify-center gap-2 rounded px-8 py-2 text-sm font-medium transition-all duration-200 border',
                                        manualCard.cardStatus === 'open'
                                            ? 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-600 text-slate-800 dark:text-slate-100 shadow-sm'
                                            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300',
                                    ]"
                                >
                                    <Icon
                                        icon="card-check"
                                        class="h-5 w-5"
                                    />
                                    Open
                                </button>
                                <!-- Closed button -->
                                <button
                                    type="button"
                                    @click="
                                        manualCard.cardStatus = 'closed'
                                    "
                                    :class="[
                                        'flex flex-1 items-center justify-center gap-2 rounded px-8 py-2 text-sm font-medium transition-all duration-200 border',
                                        manualCard.cardStatus === 'closed'
                                            ? 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-600 text-slate-800 dark:text-slate-100 shadow-sm'
                                            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300',
                                    ]"
                                >
                                    <Icon
                                        icon="card-closed"
                                        class="h-5 w-5"
                                    />
                                    Closed
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Date opened and Date closed -->
                    <div
                        class="flex flex-col gap-4 self-stretch sm:flex-row"
                    >
                        <!-- Date opened -->
                        <div class="flex-1">
                            <UiInput
                                id="date_opened"
                                name="date_opened"
                                placeholder="Select date"
                                :options="{
                                    icon: 'calendar',
                                    iconWrapperClass:
                                        'border-r border-slate-200 dark:border-slate-600 h-full pr-3',
                                }"
                                :disabled="
                                    manualCard.opened_long_ago ||
                                    manualCard.cardStatus === 'closed'
                                "
                                :errors="fieldErrors"
                            >
                                <template #label>
                                    <label
                                        for="date_opened"
                                        class="block text-sm font-medium leading-tight mb-2"
                                    >
                                        <span
                                            class="text-sm font-medium leading-5"
                                            :class="
                                                manualCard.opened_long_ago ||
                                                manualCard.cardStatus ===
                                                    'closed'
                                                    ? 'text-slate-400'
                                                    : 'text-slate-600 dark:text-slate-300'
                                            "
                                        >
                                            Date opened
                                            {{
                                                manualCard.cardStatus ===
                                                'open'
                                                    ? ''
                                                    : '(optional)'
                                            }}
                                        </span>
                                    </label>
                                </template>
                                <template #input>
                                    <flat-pickr
                                        id="date-opened"
                                        :config="dateConfig"
                                        class="date-picker flatpickr-input form-input active border shadow-sm !pr-9 !pl-[54px] w-full disabled:cursor-not-allowed placeholder:text-slate-500 dark:placeholder:text-slate-400 placeholder:text-sm placeholder:font-normal placeholder:leading-5"
                                        :class="
                                            fieldErrors.date_opened
                                                ? '!border-red-500'
                                                : 'border-slate-200 dark:border-slate-600'
                                        "
                                        v-model="manualCard.dateOpened"
                                        placeholder="Select date"
                                        :disabled="
                                            manualCard.opened_long_ago ||
                                            manualCard.cardStatus ===
                                                'closed'
                                        "
                                        @focus="
                                            $emit('clearFieldError', 'date_opened')
                                        "
                                    ></flat-pickr>
                                </template>
                                <template #error>
                                    <transition name="fade">
                                        <div
                                            class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                                            role="alert"
                                            v-if="fieldErrors.date_opened"
                                        >
                                            <Icon icon="caution" />
                                            {{ fieldErrors.date_opened }}
                                        </div>
                                    </transition>
                                </template>
                            </UiInput>
                            <div class="flex items-center mt-2">
                                <input
                                    type="checkbox"
                                    class="form-checkbox mr-1 cursor-pointer"
                                    name="opened_long_ago"
                                    value="1"
                                    v-model="manualCard.opened_long_ago"
                                    id="opened_long_ago"
                                    :disabled="
                                        manualCard.cardStatus === 'closed'
                                    "
                                />
                                <label
                                    for="opened_long_ago"
                                    class="sr-only"
                                >
                                    I'm not sure (it was a few years ago)
                                </label>
                                <span
                                    class="ml-2 leading-none text-sm"
                                    :class="
                                        manualCard.cardStatus === 'closed'
                                            ? 'text-slate-400'
                                            : ''
                                    "
                                >
                                    I'm not sure (it was a few years ago)
                                </span>
                            </div>
                        </div>

                        <!-- Date closed -->
                        <div class="flex-1">
                            <UiInput
                                id="date_closed"
                                name="date_closed"
                                placeholder="Select date"
                                :options="{
                                    icon: 'calendar',
                                    iconWrapperClass:
                                        'border-r border-slate-200 dark:border-slate-600 h-full pr-3',
                                }"
                                :disabled="
                                    manualCard.cardStatus !== 'closed'
                                "
                                :errors="fieldErrors"
                            >
                                <template #label>
                                    <label
                                        for="date_closed"
                                        class="block text-sm font-medium leading-tight mb-2"
                                    >
                                        <span
                                            class="text-sm font-medium leading-5"
                                            :class="
                                                manualCard.cardStatus ===
                                                'closed'
                                                    ? 'text-slate-600 dark:text-slate-300'
                                                    : 'text-slate-400'
                                            "
                                        >
                                            Date closed
                                            {{
                                                manualCard.cardStatus ===
                                                'closed'
                                                    ? ''
                                                    : '(optional)'
                                            }}
                                        </span>
                                    </label>
                                </template>
                                <template #input>
                                    <flat-pickr
                                        id="date-closed"
                                        :config="dateConfig"
                                        class="date-picker flatpickr-input form-input active border shadow-sm !pr-9 !pl-[54px] w-full disabled:cursor-not-allowed placeholder:text-slate-500 dark:placeholder:text-slate-400 placeholder:text-sm placeholder:font-normal placeholder:leading-5"
                                        :class="
                                            fieldErrors.date_closed
                                                ? '!border-red-500'
                                                : 'border-slate-200 dark:border-slate-600'
                                        "
                                        v-model="manualCard.dateClosed"
                                        placeholder="Select date"
                                        :disabled="
                                            manualCard.cardStatus !==
                                            'closed'
                                        "
                                        @focus="
                                            $emit('clearFieldError', 'date_closed')
                                        "
                                    ></flat-pickr>
                                </template>
                                <template #error>
                                    <transition name="fade">
                                        <div
                                            class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                                            role="alert"
                                            v-if="fieldErrors.date_closed"
                                        >
                                            <Icon icon="caution" />
                                            {{ fieldErrors.date_closed }}
                                        </div>
                                    </transition>
                                </template>
                            </UiInput>
                        </div>
                    </div>

                    <!-- Annual fee and Credit limit -->
                    <div
                        class="flex flex-col gap-4 self-stretch sm:flex-row"
                    >
                        <!-- Annual fee -->
                        <div class="flex-1">
                            <label
                                for="annual_fee"
                                class="block text-sm font-medium leading-tight mb-2 text-slate-600 dark:text-slate-300"
                            >
                                Annual fee
                            </label>
                            <div class="relative">
                                <span
                                    class="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 dark:text-slate-400"
                                >
                                    $
                                </span>
                                <input
                                    type="number"
                                    step="0.01"
                                    class="form-input w-full !pl-8 placeholder:text-slate-500 dark:placeholder:text-slate-400 placeholder:text-sm placeholder:font-normal placeholder:leading-5"
                                    v-model="manualCard.annualFee"
                                    placeholder="95.00"
                                    :disabled="
                                        manualChosenCard &&
                                        !manualCard.user_managed_annual_fee
                                    "
                                />
                            </div>
                            <!-- Override annual fee checkbox -->
                            <div
                                v-if="manualChosenCard"
                                class="flex items-center mt-2"
                            >
                                <input
                                    type="checkbox"
                                    class="form-checkbox mr-1 cursor-pointer"
                                    name="user_managed_annual_fee"
                                    id="user_managed_annual_fee"
                                    v-model="
                                        manualCard.user_managed_annual_fee
                                    "
                                    value="1"
                                />
                                <label
                                    for="user_managed_annual_fee"
                                    class="sr-only"
                                >
                                    Override annual fee
                                </label>
                                <span class="ml-2 leading-none text-sm">
                                    Override annual fee
                                </span>
                            </div>
                        </div>

                        <!-- Credit limit -->
                        <div class="flex-1">
                            <label
                                for="credit_limit"
                                class="block text-sm font-medium leading-tight mb-2 text-slate-600 dark:text-slate-300"
                            >
                                Credit limit (optional)
                            </label>
                            <div class="relative">
                                <span
                                    class="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 dark:text-slate-400"
                                >
                                    $
                                </span>
                                <input
                                    type="text"
                                    class="form-input w-full !pl-8 placeholder:text-slate-500 dark:placeholder:text-slate-400 placeholder:text-sm placeholder:font-normal placeholder:leading-5"
                                    :value="formattedCreditLimit"
                                    @input="handleCreditLimitInput"
                                    placeholder="Enter credit limit"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Account type -->
                    <div class="flex flex-col items-start self-stretch">
                        <label
                            for="account_type"
                            class="block text-sm font-medium leading-tight mb-2 text-slate-600 dark:text-slate-300"
                        >
                            Account type
                        </label>
                        <div class="flex flex-col gap-2">
                            <label class="flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="account_type"
                                    value="cardholder"
                                    id="cardholder"
                                    v-model="manualCard.accountType"
                                    @change="$emit('clearFieldError', 'account_type')"
                                    class="form-radio"
                                />
                                <span class="text-slate-800 dark:text-slate-100 text-sm">
                                    Cardholder
                                </span>
                            </label>
                            <label class="flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="account_type"
                                    value="authorized_user"
                                    id="authorized_user"
                                    v-model="manualCard.accountType"
                                    @change="$emit('clearFieldError', 'account_type')"
                                    class="form-radio"
                                />
                                <span class="text-slate-800 dark:text-slate-100 text-sm">
                                    Authorized user
                                </span>
                            </label>
                        </div>
                        <transition
                            enter-active-class="transition duration-150 ease-out"
                            enter-from-class="opacity-0 -translate-y-1"
                            enter-to-class="opacity-100 translate-y-0"
                            leave-active-class="transition duration-100 ease-in"
                            leave-from-class="opacity-100"
                            leave-to-class="opacity-0"
                        >
                            <div
                                v-if="fieldErrors.account_type"
                                class="text-red-500 text-sm mt-2"
                            >
                                {{ fieldErrors.account_type }}
                            </div>
                        </transition>
                    </div>

                    <!-- Entity (only for business cards) -->
                    <div
                        class="flex flex-col items-start self-stretch"
                        v-if="
                            manualChosenCard &&
                            manualChosenCard.type === 'business'
                        "
                    >
                        <label
                            for="business_id"
                            class="block text-sm font-medium leading-tight mb-2 text-slate-600 dark:text-slate-300"
                        >
                            Entity
                            <span class="text-slate-500 dark:text-slate-400 font-normal">
                                (optional)
                            </span>
                        </label>
                        <div
                            class="relative w-full"
                            data-dropdown="business"
                        >
                            <button
                                type="button"
                                @click="toggleBusinessDropdown"
                                ref="businessDropdownBtn"
                                class="form-input w-full text-left flex items-center justify-between px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded shadow-sm text-left focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-sm font-normal leading-5 placeholder:text-slate-500 dark:placeholder:text-slate-400 placeholder:text-sm placeholder:font-normal placeholder:leading-5"
                                :class="{
                                    'text-slate-500 dark:text-slate-400': !selectedBusiness,
                                }"
                            >
                                <div class="flex items-center gap-2">
                                    <Icon
                                        icon="building"
                                        class="w-5 h-5 text-slate-800 dark:text-slate-100"
                                    />
                                    <span
                                        class="text-sm font-normal leading-5"
                                        :class="
                                            manualCard.business_id
                                                ? 'text-slate-800 dark:text-slate-100'
                                                : 'text-slate-500 dark:text-slate-400'
                                        "
                                    >
                                        {{
                                            selectedBusinessName ||
                                            'Select entity'
                                        }}
                                    </span>
                                </div>
                                <Icon
                                    icon="chevron-down"
                                    class="w-4 h-4 text-slate-400"
                                    :class="{
                                        'rotate-180': showBusinessDropdown,
                                    }"
                                />
                            </button>
                        </div>
                        <!-- Teleport dropdown to body -->
                        <teleport to="body">
                            <div
                                v-if="showBusinessDropdown"
                                class="fixed z-[9999] bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-md shadow-lg"
                                data-dropdown-options="business"
                                :style="{
                                    top: businessDropdownStyles.top + 'px',
                                    left:
                                        businessDropdownStyles.left + 'px',
                                    width:
                                        businessDropdownStyles.width + 'px',
                                }"
                            >
                                <!-- No entity option -->
                                <div
                                    v-if="businesses.length === 0"
                                    @click="selectBusiness(null)"
                                    class="px-4 py-2.5 text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 hover:bg-slate-50 dark:hover:bg-slate-600 cursor-pointer first:rounded-t-md last:rounded-b-md"
                                >
                                    No entity
                                </div>
                                <!-- Business entities -->
                                <div
                                    v-for="business in businesses"
                                    :key="business.id"
                                    @click="selectBusiness(business)"
                                    class="px-4 py-2.5 text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 hover:bg-slate-50 dark:hover:bg-slate-600 cursor-pointer flex items-center gap-2 first:rounded-t-md last:rounded-b-md"
                                >
                                    <Icon icon="building" class="w-5 h-5" />
                                    {{ business.name }}
                                </div>
                                <!-- Add new entity option -->
                                <div
                                    @click="openAddEntityModal"
                                    class="px-4 py-2.5 text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 hover:bg-slate-50 dark:hover:bg-slate-600 cursor-pointer flex items-center gap-2 first:rounded-t-md last:rounded-b-md"
                                >
                                    <Icon icon="plus" class="w-4 h-4" />
                                    Add new entity
                                </div>
                            </div>
                        </teleport>
                    </div>
                </div>

                <!-- Mobile bottom bar -->
                <div
                    class="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] z-[1000]"
                >
                    <div class="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            @click.prevent="$emit('gotoManualStep', 1)"
                            class="w-full py-3 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700"
                            id="hs-wallet-manual-card-entry-back-mobile-step3"
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            @click.prevent="
                                manualCard.accountType === 'authorized_user'
                                    ? $emit('addCard')
                                    : $emit('gotoManualStep', 4)
                            "
                            class="w-full py-3 rounded-md bg-slate-800 text-white text-sm font-medium leading-5 hover:bg-slate-700 active:bg-slate-700"
                            id="hs-wallet-manual-card-entry-next-mobile-step3"
                        >
                            {{
                                manualCard.accountType === 'authorized_user'
                                    ? 'Finish'
                                    : 'Next'
                            }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div
                class="hidden md:flex justify-between items-center p-6 bg-white dark:bg-slate-800 flex-shrink-0 mt-auto w-full"
            >
                <!-- Back Button -->
                <button
                    type="button"
                    @click="$emit('gotoManualStep', 1)"
                    class="flex py-2.5 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                    Back
                </button>

                <!-- Progress indicator -->
                <div class="hidden md:flex gap-2">
                    <template
                        v-if="manualCard.accountType === 'authorized_user'"
                    >
                        <div class="w-6 h-1 rounded-sm bg-slate-200"></div>
                        <div class="w-6 h-1 rounded-sm bg-slate-800"></div>
                    </template>
                    <template v-else>
                        <div class="w-6 h-1 rounded-sm bg-slate-200"></div>
                        <div class="w-6 h-1 rounded-sm bg-slate-800"></div>
                        <div class="w-6 h-1 rounded-sm bg-slate-200"></div>
                    </template>
                </div>

                <!-- Right buttons -->
                <div class="flex gap-3">
                    <!-- Cancel Button (only for authorized user) -->
                    <button
                        v-if="manualCard.accountType === 'authorized_user'"
                        type="button"
                        @click="$emit('close')"
                        class="flex py-2.5 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                        Cancel
                    </button>

                    <!-- Next/Finish Button -->
                    <button
                        type="button"
                        @click="
                            manualCard.accountType === 'authorized_user'
                                ? $emit('addCard')
                                : $emit('gotoManualStep', 4)
                        "
                        class="flex py-2.5 px-4 justify-center items-center gap-2 rounded bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-white text-sm font-medium leading-5 hover:bg-slate-700 transition-colors"
                    >
                        {{
                            manualCard.accountType === 'authorized_user'
                                ? 'Finish'
                                : 'Next'
                        }}
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';
import Errors from '@/components/errors.vue';

const props = defineProps({
    manualCard: { type: Object, required: true },
    manualChosenCard: { type: Object, default: null },
    fieldErrors: { type: Object, required: true },
    errors: { type: Array, required: true },
    currentStep: { type: Number, required: true },
    selectedCardAndUserHeader: { type: String, default: '' },
    businesses: { type: Array, default: () => [] },
    accountUserForCard: { type: Number, default: null },
});

const emit = defineEmits([
    'close',
    'gotoManualStep',
    'addCard',
    'clearFieldError',
    'openAddEntityModal',
    'update:creditLimit',
    'update:business_id',
]);

const cdnUrl = computed(() => window.cdnUrl);

const dateConfig = {
    dateFormat: 'M j, Y',
    maxDate: 'today',
};

// Business dropdown logic
const showBusinessDropdown = ref(false);
const businessDropdownStyles = ref({ top: 0, left: 0, width: 0 });
const businessDropdownBtn = ref(null);

const selectedBusiness = computed(() => {
    if (!props.manualCard.business_id) return null;
    return props.businesses.find((b) => b.id === props.manualCard.business_id);
});

const selectedBusinessName = computed(() => {
    return selectedBusiness.value ? selectedBusiness.value.name : null;
});

const formattedCreditLimit = computed(() => {
    if (!props.manualCard.creditLimit) return '';
    const numericValue = String(props.manualCard.creditLimit).replace(
        /\D/g,
        ''
    );
    if (!numericValue) return '';
    return Number(numericValue).toLocaleString('en-US');
});

function handleCreditLimitInput(event) {
    const numericValue = event.target.value.replace(/\D/g, '');
    emit('update:creditLimit', numericValue);
}

function updateBusinessDropdownPosition() {
    nextTick(() => {
        const btn = businessDropdownBtn.value;
        if (!btn) return;
        const rect = btn.getBoundingClientRect();
        businessDropdownStyles.value = {
            top: Math.round(rect.bottom + 4),
            left: Math.round(rect.left),
            width: Math.round(rect.width),
        };
    });
}

function toggleBusinessDropdown() {
    showBusinessDropdown.value = !showBusinessDropdown.value;
    if (showBusinessDropdown.value) {
        updateBusinessDropdownPosition();
        window.addEventListener(
            'scroll',
            updateBusinessDropdownPosition,
            true
        );
        window.addEventListener(
            'resize',
            updateBusinessDropdownPosition,
            true
        );
    } else {
        window.removeEventListener(
            'scroll',
            updateBusinessDropdownPosition,
            true
        );
        window.removeEventListener(
            'resize',
            updateBusinessDropdownPosition,
            true
        );
    }
}

function selectBusiness(business) {
    emit('update:business_id', business ? business.id : null);
    showBusinessDropdown.value = false;
}

function openAddEntityModal() {
    showBusinessDropdown.value = false;
    emit('openAddEntityModal');
}

function handleClickOutside(event) {
    if (showBusinessDropdown.value) {
        const inBusinessTrigger = event.target.closest(
            '[data-dropdown="business"]'
        );
        const inBusinessMenu = event.target.closest(
            '[data-dropdown-options="business"]'
        );
        if (!inBusinessTrigger && !inBusinessMenu) {
            showBusinessDropdown.value = false;
            window.removeEventListener(
                'scroll',
                updateBusinessDropdownPosition,
                true
            );
            window.removeEventListener(
                'resize',
                updateBusinessDropdownPosition,
                true
            );
        }
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
    window.removeEventListener('scroll', updateBusinessDropdownPosition, true);
    window.removeEventListener('resize', updateBusinessDropdownPosition, true);
});
</script>
